import type {
    DefaultParameterMapNodeResult,
    DefaultParameterMapResult,
    ElastixNodeResult,
    ElastixOptions,
    ElastixResult,
} from '@itk-wasm/elastix';
import { mat3, mat4, vec3 } from 'gl-matrix';
import {
    Image,
    ImageType,
    IntTypes,
    PixelTypes,
    type JsonCompatible,
} from 'itk-wasm';
import { Image3DArray } from './image3DArray';
import { convertTranslationToOffset, matrixFromRigidTransform } from './utils';

/**
 * Conditional imports for Elastix based on runtime environment.
 * Browser and Node.js versions have different function names but compatible result types.
 * Uses lazy initialization to avoid top-level await.
 */
let defaultParameterMap: (
    transformName: string
) => Promise<DefaultParameterMapResult | DefaultParameterMapNodeResult>;
let elastix: (
    parameterObject: JsonCompatible,
    options?: ElastixOptions
) => Promise<ElastixResult | ElastixNodeResult>;
let setElastixPipelinesBaseUrl: (baseUrl: string | URL) => void;

let elastixModulePromise: Promise<void> | null = null;

async function loadElastixModule(): Promise<void> {
    if (elastixModulePromise) {
        return elastixModulePromise;
    }

    elastixModulePromise = (async () => {
        if (typeof process !== 'undefined' && process.versions?.node) {
            // Node.js environment
            const elastixNode = await import('@itk-wasm/elastix');
            defaultParameterMap = elastixNode.defaultParameterMapNode;
            elastix = elastixNode.elastixNode;
            setElastixPipelinesBaseUrl = elastixNode.setPipelinesBaseUrl;
        } else {
            // Browser environment
            const elastixBrowser = await import('@itk-wasm/elastix');
            defaultParameterMap = elastixBrowser.defaultParameterMap;
            elastix = elastixBrowser.elastix;
            setElastixPipelinesBaseUrl = elastixBrowser.setPipelinesBaseUrl;
        }
    })();

    return elastixModulePromise;
}

/**
 * interface for elasitx registration results.
 */
export interface RegistrationResult {
    registeredImage: Image;
    transformParameters: JsonCompatible;
}

/**
 * Initialize ITK-Wasm pipeline URLs for DICOM and Elastix modules.
 * This is called at app startup (see src/index.tsx).
 */
export async function initializeItkWasmPipelines(): Promise<void> {
    await loadElastixModule();

    const viteBaseUrl = import.meta.env.BASE_URL || '/';
    const elastixPipelinesUrl = new URL(
        `${viteBaseUrl}pipelines/elastix`,
        document.location.origin
    ).href;

    setElastixPipelinesBaseUrl(elastixPipelinesUrl);
}

/**
 * Convert Image3DArray to ITK Image.
 *
 * @param image
 * @returns itk Image object
 */
export function Image3DArrayToITKImage(image: Image3DArray): Image {
    const imageType = new ImageType(
        3, // dimension
        IntTypes.Int16, // componentType (Image3D always uses Int16Array)
        PixelTypes.Scalar, // pixelType
        1 // components
    );
    const outImage = new Image(imageType);
    outImage.origin = Array.from(image.grid.getCornerVoxelCenterPosition());
    outImage.spacing = Array.from(image.grid.getRes());
    outImage.size = Array.from(image.grid.getSize());
    const orientationInverse = image.grid.getOrientationInverse();
    outImage.direction = new Float64Array(orientationInverse);
    outImage.data = image.pixelData;
    return outImage;
}

/**
 * Get parameter map for elastix registration.
 * The default map is "rigid" (see elastix docs)
 * elastixSettings can be used to override default settings.
 *
 * @param elastixSettings - key-value pairs to override default elastix settings
 * @returns elastix parameterMapResult object
 */
async function generateElastixParameterMap(
    elastixSettings: Record<string, string> = {}
): Promise<Record<string, string[]>> {
    await loadElastixModule();
    const parameterMapResult = await defaultParameterMap('rigid');
    const parameterMap = parameterMapResult.parameterMap as Record<
        string,
        string[]
    >;

    // // Parameters to save compute and disk IO operations
    parameterMap.WriteResultImage = ['false'];
    parameterMap.AutomaticTransformInitialization = ['false']; // not needed as we resample images beforehand
    parameterMap.WriteTransformParametersEachIteration = ['false'];
    parameterMap.WriteTransformParametersEachResolution = ['false'];
    parameterMap.UseDirectionCosines = ['true'];
    parameterMap.Interpolator = ['LinearInterpolator'];

    // Rigid registration specific settings.
    parameterMap.Transform = ['EulerTransform'];
    parameterMap.AutomaticScalesEstimation = ['true'];

    // Mattes MI  metric.
    // Number of bins is important for convergence and accuracy.
    // Probably should be between 32 and 128.
    parameterMap.Metric = ['AdvancedMattesMutualInformation'];
    parameterMap.NumberOfHistogramBins = ['64'];

    // resolution settings.
    // adding more steps to the pyramid may both increase and decrease time.
    // Increase for faster, less accurate results.
    // Decrease for slower, more accurate results.
    // Note, that as of now resampling is done outside elastix,
    // so changing the final size there might be a better option.
    parameterMap.NumberOfResolutions = ['1'];
    parameterMap.ImagePyramidSchedule = ['1', '1', '1'];

    // Optimizer settings.
    parameterMap.Optimizer = ['AdaptiveStochasticGradientDescent'];
    // Maximum number of iterations.
    // ASGD uses all iterations, so more iterations means more compute time.
    parameterMap.MaximumNumberOfIterations = ['1000'];
    parameterMap.AutomaticParameterEstimation = ['true'];
    parameterMap.UseAdaptiveStepSizes = ['true'];
    // parameterMap.MaximumStepLength = ['2.0']; // cap the aggressive step sizes
    parameterMap.NewSamplesEveryIteration = ['true'];
    parameterMap.NumberOfSpatialSamples = ['4096'];
    parameterMap.ImageSampler = ['RandomCoordinate'];

    // // Another optimzaer setting (no adaptive step but supports early stopping)
    // parameterMap.Optimizer = ['RegularStepGradientDescent'];
    // parameterMap.MaximumNumberOfIterations = ['1000'];
    // parameterMap.RelaxationFactor = ['0.5'];
    // parameterMap.MinimumGradientMagnitude = ['1e-6'];
    // parameterMap.MinimumStepLength = ['1e-4'];
    // parameterMap.ImageSampler = ['Grid']; // RSGD is probably better with grid sampling
    // parameterMap.SampleGridSpacing = ['1', '1', '1'];

    // Apply custom settings (overrides defaults)
    for (const [key, value] of Object.entries(elastixSettings)) {
        parameterMap[key] = [value];
    }

    return parameterMap;
}

/**
 * Parse Elastix Rigid (Euler) transform parameters into 4x4 matrix.
 *
 * Note:
 * ITK (and elastix) deals with inverse transforms.
 * This function doesn't do this invertion
 *
 * @param params - Elastix transform parameters object
 * @returns 4x4 transformation matrix (mat4)
 */
function parseElastixRigidTransformParameters(
    params: Record<string, unknown>
): mat4 {
    // Parse transform parameters [rx, ry, rz, tx, ty, tz]
    const [rx, ry, rz, tx, ty, tz] = (
        params.TransformParameters as string[]
    ).map(parseFloat);

    // Parse center of rotation [x, y, z]
    const centerOfRotation = (params.CenterOfRotationPoint as string[]).map(
        parseFloat
    ) as [number, number, number];

    return matrixFromRigidTransform(
        vec3.fromValues(rx, ry, rz),
        vec3.fromValues(tx, ty, tz),
        vec3.fromValues(
            centerOfRotation[0],
            centerOfRotation[1],
            centerOfRotation[2]
        )
    );

    // // Build rotation matrix (Z-Y-X order)
    // const rotation = mat4.create();
    // mat4.rotateZ(rotation, rotation, rz);
    // mat4.rotateY(rotation, rotation, ry);
    // mat4.rotateX(rotation, rotation, rx);

    // const offset = convertTranslationToOffset(
    //     mat3.fromMat4(mat3.create(), rotation),
    //     [tx, ty, tz],
    //     centerOfRotation
    // );
    // rotation[12] = offset[0];
    // rotation[13] = offset[1];
    // rotation[14] = offset[2];
    // rotation[15] = 1;
    // return rotation;
}

/**
 * Parse Elastix Affine transform parameters into 4x4 matrix.
 *
 * Note:
 * ITK (and elastix) deals with inverse transforms.
 * This function doesn't do this invertion
 *
 * @param params - Elastix transform parameters object
 * @returns 4x4 transformation matrix (mat4)
 */

function parseElastixAffineTransformParameters(
    params: Record<string, unknown>
): mat4 {
    // params.TransformParameters is an array of 12 strings: [m00, m01, m02, m10, m11, m12, m20, m21, m22, tx, ty, tz]
    const values = (params.TransformParameters as string[]).map(parseFloat);
    if (values.length !== 12) {
        throw new Error('AffineTransform requires 12 parameters');
    }

    // gl-matrix mat4 layout:
    // 0 4 8 12
    // 1 5 9 13
    // 2 6 10 14
    // 3 7 11 15

    // itk (and elastix) AffineTransform layout:
    // [m00, m01, m02, m10, m11, m12, m20, m21, m22, tx, ty, tz]
    // So the matrix is:
    // 0 1 2 | 9
    // 3 4 5 | 10
    // 6 7 8 | 11
    // - - - | -

    const mat = mat4.create();
    mat[0] = values[0]; // m00
    mat[1] = values[3]; // m10
    mat[2] = values[6]; // m20
    mat[3] = 0;

    mat[4] = values[1]; // m01
    mat[5] = values[4]; // m11
    mat[6] = values[7]; // m21
    mat[7] = 0;

    mat[8] = values[2]; // m02
    mat[9] = values[5]; // m12
    mat[10] = values[8]; // m22
    mat[11] = 0;

    const centerOfRotation = (params.CenterOfRotationPoint as string[]).map(
        parseFloat
    ) as [number, number, number];

    // elastix outputs translation vector, not the offset
    const offset = convertTranslationToOffset(
        mat3.fromMat4(mat3.create(), mat),
        [values[9], values[10], values[11]], // tx, ty, tz
        centerOfRotation
    );

    mat[12] = offset[0]; // tx
    mat[13] = offset[1]; // ty
    mat[14] = offset[2]; // tz
    mat[15] = 1;

    return mat;
}

/**
 * Router function to parse different elastix transform types.
 * Currently supports only Rigid (EulerTransform) and Affine transforms.
 *
 * @param params
 * @returns 4x4 transformation matrix (mat4)
 */
function parseElastixTransformParameters(
    params: Record<string, unknown>
): mat4 {
    // const params = (transformParameters as Record<string, unknown>[])[0];
    if (params.Transform == 'EulerTransform') {
        return parseElastixRigidTransformParameters(params);
    } else if (params.Transform == 'AffineTransform') {
        return parseElastixAffineTransformParameters(params);
    } else {
        throw new Error(`Unsupported transform type: ${params.Transform}`);
    }
}

/**
 * Convert Elastix registration result to 4x4 m transformation matrix.
 *
 * @param transformParameters - Transform parameters from elastix registration result
 * @returns 4x4 transformation matrix (mat4)
 */

export function createRigidRegistrationFromElastix(
    transformParameters: JsonCompatible
): mat4 {
    let finalMatrix: mat4 = mat4.create();
    // Elastix can return multiple transform parameter sets
    // Parsing them in order and combining the resulting matrices
    // If non-matrix transform types are encountered, an error is thrown
    for (const paramSet of transformParameters as Record<string, unknown>[]) {
        const transformMatrix = parseElastixTransformParameters(paramSet);
        finalMatrix = mat4.multiply(finalMatrix, transformMatrix, finalMatrix);
    }
    // ITK (and elastix) deals with inverse transforms, so we need to invert the final matrix
    finalMatrix = mat4.invert(mat4.create(), finalMatrix)!;
    return finalMatrix;
}

/**
 * Run elastix registration on two Image3DArray images.
 * There resulting transform maps movingImage to fixedImage.
 * This is compatibble with the rest of the Workspace+
 * and inverse of the transform provided by Elastix module.
 *
 * @param fixedImage - fixed (primary) image
 * @param movingImage - moving (secondary) image
 * @param elastixSettings - elastix settings to override defaults
 * @returns 4x4 transformation matrix (mat4) resulting from registration.
 *
 */
export async function runElastixRegistration(
    fixedImage: Image3DArray,
    movingImage: Image3DArray,
    elastixSettings: Record<string, string> = {}
): Promise<mat4> {
    await loadElastixModule();
    const fixedImageITK = Image3DArrayToITKImage(fixedImage);
    const movingImageITK = Image3DArrayToITKImage(movingImage);
    const parameterMap = await generateElastixParameterMap(elastixSettings);

    try {
        const elastixOptions: {
            fixed: Image;
            moving: Image;
            initialTransformParameterObject?: JsonCompatible;
        } = {
            fixed: fixedImageITK,
            moving: movingImageITK,
        };

        const registrationResult = await elastix(
            [parameterMap],
            elastixOptions
        );
        const elastixTransform = createRigidRegistrationFromElastix(
            registrationResult.transformParameterObject
        );
        return elastixTransform;
    } catch (error) {
        console.error('Error:', error);
        throw error;
    }
}
